import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import { formatDate } from '../../../../helpers/formatDate.js';
import { useTranslation } from 'react-i18next';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';

const useStyles = makeStyles(() => ({
  formControl: {
    width: '300px',
    '& .MuiInputBase-root': {
      fontSize: '12px',
      height: 22,
    },
    '& .MuiOutlinedInput-root': {
      height: 26,
      minHeight: 20,
      borderRadius: 4,
    },
    '& .MuiOutlinedInput-input': {
      padding: '1px 4px',
      height: 19,
      minHeight: 18,
    },
    '& .MuiSelect-nativeInput': {
      fontSize: '12px',
    },
  },

  select: {
    fontSize: '12px',
    padding: 0,
    textTransform: 'uppercase',
    '& .MuiSelect-select': {
      textTransform: 'uppercase',
      display: 'flex',
      alignItems: 'center',
      fontSize: '12px',
    },
  },

  selectValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  selectMenuItem: {
    fontSize: 12,
    minHeight: 28,
    textTransform: 'uppercase',
  },
}));

const nativeLikeSelectMenuProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
  PaperProps: {
    style: {
      marginTop: 2,
      boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
    },
  },
  MenuListProps: {
    dense: true,
    style: { padding: 0 },
  },
};

const DatePicker = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [datePreset, setDatePreset] = useState(props.selectedRange || 'day');

  const today = new Date();
  const formatToDDMMYYYY = (date) =>
    `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(
      2,
      '0',
    )}/${date.getFullYear()}`;

  const todayFormatted = formatToDDMMYYYY(today);
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 6);
  const lastWeekFormatted = formatToDDMMYYYY(lastWeek);

  useEffect(() => {
    setDatePreset(props.selectedRange || 'day');
  }, [props.selectedRange]);

  const handleDatePreset = (newPreset) => {
    const todayBegin = formatDate(new Date(), '00:00');
    const todayEnd = formatDate(new Date(), '23:59');

    switch (newPreset) {
      case 'day':
        props.setDates([todayBegin, todayEnd]);
        break;
      case 'week': {
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - 6);
        props.setDates([formatDate(weekStart, '00:00'), todayEnd]);
        break;
      }
      case 'all':
        props.setDates(null);
        break;
      case 'custom':
        props.setDates(props.customDate);
        break;
      default:
        throw new Error('Incorrect date preset.');
    }

    props.setSelectedRange(newPreset);
    setDatePreset(newPreset);
  };

  const renderPresetLabel = (selected) => {
    if (selected === 'day') return `${t('DatePicker.day')} ${todayFormatted}`;
    if (selected === 'week') return `${t('DatePicker.week')} ${lastWeekFormatted} - ${todayFormatted}`;
    if (selected === 'all') return t('DatePicker.all');
    return t('DatePicker.custom');
  };

  return (
    <FormControl variant='outlined' size='small' className={classes.formControl}>
      <Select
        id='date-selector-id'
        className={classes.select}
        value={datePreset}
        onChange={(e) => handleDatePreset(e.target.value)}
        MenuProps={nativeLikeSelectMenuProps}
        renderValue={(selected) => (
          <div className={classes.selectValue}>
            <CalendarTodayIcon style={{ fontSize: 14 }} />
            {renderPresetLabel(selected)}
          </div>
        )}>
        <MenuItem className={classes.selectMenuItem} value='day' id='date-selector-day'>
          {`${t('DatePicker.day')} ${todayFormatted}`}
        </MenuItem>
        <MenuItem className={classes.selectMenuItem} value='week' id='date-selector-week'>
          {`${t('DatePicker.week')} ${lastWeekFormatted} - ${todayFormatted}`}
        </MenuItem>
        <MenuItem className={classes.selectMenuItem} value='all' id='date-selector-all'>
          {t('DatePicker.all')}
        </MenuItem>
        <MenuItem className={classes.selectMenuItem} value='custom' id='date-selector-custom'>
          {t('DatePicker.custom')}
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default DatePicker;
